/**
 * semantic_scholar_search - Search Academic Papers via Semantic Scholar API
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';

const SEMANTIC_SCHOLAR_BASE = 'https://api.semanticscholar.org/graph/v1';

export const semantic_scholar_search: SimpleDocCommand = {
  id: 'semantic_scholar_search',
  logicalName: 'semantic_scholar_search',
  version: 'v1',
  
  description: `Search and retrieve academic papers from Semantic Scholar's database of 200+ million papers.

**WHAT IT DOES:**
Searches Semantic Scholar's comprehensive academic database. Access paper metadata, citations, references, author information, and full text links.

**OPERATIONS:**
- search_papers: Search papers by query string
- get_paper: Get detailed paper information by ID, DOI, ArXiv ID, or other identifier
- get_author: Get author details and their publications
- get_citations: Get papers that cite a specific paper
- get_references: Get papers referenced by a specific paper

**PARAMETERS:**
- operation (required): The operation to perform (see OPERATIONS above)
- query (for search_papers): Search query string (e.g., "transformer neural networks", "climate change mitigation")
- paperId (for paper operations): Paper identifier - supports DOI, ArXiv ID, S2 paper ID, etc.
  Examples: "10.1038/nature14539", "arXiv:1706.03762", "649def34f8be52c8b66281af98ae884c09aef38b"
- authorId (for get_author): Semantic Scholar author ID (numeric or hash)
- limit (optional): Max results to return (1-100, default: 10)
- offset (optional): Pagination offset for search results (default: 0)
- fields (optional): Array of fields to include in results
  Common fields: "title", "authors", "year", "abstract", "citationCount", "referenceCount", 
  "publicationDate", "venue", "openAccessPdf", "externalIds", "url", "fieldsOfStudy"
- year (optional): Filter by publication year (e.g., "2020" or "2015-2020")
- fieldsOfStudy (optional): Filter by field (e.g., "Computer Science", "Medicine", "Physics")
- openAccessPdf (optional): Only return papers with open access PDFs (true/false)

**RETURNS:**
Paper objects with requested fields:
- paperId: Semantic Scholar paper ID
- title: Paper title
- authors: Array of author objects (name, authorId)
- year: Publication year
- abstract: Paper abstract (if available)
- citationCount: Number of citations
- referenceCount: Number of references
- venue: Publication venue (journal/conference)
- openAccessPdf: Link to open access PDF (if available)
- url: Semantic Scholar page URL
- externalIds: DOI, ArXiv ID, PubMed ID, etc.

**EXAMPLES:**

Search for transformer papers:
{ "operation": "search_papers", "query": "attention is all you need transformer", "limit": 5, "fields": ["title", "authors", "year", "abstract", "citationCount"] }

Get paper by DOI:
{ "operation": "get_paper", "paperId": "DOI:10.1038/nature14539", "fields": ["title", "authors", "abstract", "citationCount", "references"] }

Get paper by ArXiv ID:
{ "operation": "get_paper", "paperId": "arXiv:1706.03762", "fields": ["title", "authors", "year", "citationCount", "openAccessPdf"] }

Search recent ML papers with open access:
{ "operation": "search_papers", "query": "machine learning", "year": "2023-2024", "openAccessPdf": true, "limit": 10 }

Get author's publications:
{ "operation": "get_author", "authorId": "1741101", "limit": 20 }

Get citations for a paper:
{ "operation": "get_citations", "paperId": "649def34f8be52c8b66281af98ae884c09aef38b", "limit": 50 }

Get references from a paper:
{ "operation": "get_references", "paperId": "arXiv:1706.03762", "limit": 30 }

**RATE LIMITS:**
- Free tier: 100 requests per 5 minutes
- Automatically handles rate limiting with exponential backoff
- For higher limits, contact Semantic Scholar for API key

**TIPS:**
- Use specific queries for better results ("BERT pretraining" vs "machine learning")
- Filter by year to get recent papers
- Use openAccessPdf filter to only get freely available papers
- Check citationCount to find influential papers
- Use get_citations to find recent work building on a paper`,

  schema: {
    parameters: [
      {
        name: 'operation',
        type: 'string',
        description: 'Operation to perform: search_papers, get_paper, get_author, get_citations, get_references',
        required: true,
        enum: ['search_papers', 'get_paper', 'get_author', 'get_citations', 'get_references'],
        validator: (value: string) => {
          const validOps = ['search_papers', 'get_paper', 'get_author', 'get_citations', 'get_references'];
          if (!validOps.includes(value)) {
            return `Operation must be one of: ${validOps.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'query',
        type: 'string',
        description: 'Search query for search_papers operation',
        required: false,
        examples: ['transformer neural networks', 'climate change mitigation', 'CRISPR gene editing'],
        validator: (value: string, params: any) => {
          if (params.operation === 'search_papers' && !value) {
            return 'query is required for search_papers operation';
          }
          if (value && value.length > 300) {
            return 'Query too long (max 300 characters)';
          }
          return null;
        }
      },
      {
        name: 'paperId',
        type: 'string',
        description: 'Paper identifier (DOI, ArXiv ID, S2 ID, etc.) for paper operations',
        required: false,
        examples: ['DOI:10.1038/nature14539', 'arXiv:1706.03762', '649def34f8be52c8b66281af98ae884c09aef38b'],
        validator: (value: string, params: any) => {
          const paperOps = ['get_paper', 'get_citations', 'get_references'];
          if (paperOps.includes(params.operation) && !value) {
            return `paperId is required for ${params.operation} operation`;
          }
          return null;
        }
      },
      {
        name: 'authorId',
        type: 'string',
        description: 'Semantic Scholar author ID for get_author operation',
        required: false,
        examples: ['1741101', '2262347'],
        validator: (value: string, params: any) => {
          if (params.operation === 'get_author' && !value) {
            return 'authorId is required for get_author operation';
          }
          return null;
        }
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return (1-100, default: 10)',
        required: false,
        defaultValue: 10,
        examples: [5, 10, 20, 50],
        validator: (value: number) => {
          if (value === undefined || value === null) return null;
          if (value < 1 || value > 100) {
            return 'limit must be between 1 and 100';
          }
          return null;
        }
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Pagination offset for search results (default: 0)',
        required: false,
        defaultValue: 0,
        validator: (value: number) => {
          if (value !== undefined && value < 0) {
            return 'offset must be non-negative';
          }
          return null;
        }
      },
      {
        name: 'fields',
        type: 'array',
        description: 'Array of fields to include in results',
        required: false,
        examples: [
          ['title', 'authors', 'year', 'abstract'],
          ['title', 'citationCount', 'openAccessPdf'],
          ['title', 'authors', 'year', 'venue', 'externalIds']
        ],
        validator: (value: string[]) => {
          if (!value) return null;
          const validFields = [
            'title', 'authors', 'year', 'abstract', 'citationCount', 'referenceCount',
            'publicationDate', 'venue', 'openAccessPdf', 'externalIds', 'url', 
            'fieldsOfStudy', 'publicationTypes', 'publicationVenue', 'isOpenAccess',
            'influentialCitationCount', 's2FieldsOfStudy', 'embedding'
          ];
          const invalid = value.filter(f => !validFields.includes(f));
          if (invalid.length > 0) {
            return `Invalid fields: ${invalid.join(', ')}. Valid fields: ${validFields.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'year',
        type: 'string',
        description: 'Filter by publication year (e.g., "2020" or "2015-2020")',
        required: false,
        examples: ['2023', '2020-2024', '2015-2020'],
        validator: (value: string) => {
          if (!value) return null;
          // Validate year format: YYYY or YYYY-YYYY
          if (!/^\d{4}(-\d{4})?$/.test(value)) {
            return 'year must be in format YYYY or YYYY-YYYY (e.g., "2020" or "2015-2020")';
          }
          return null;
        }
      },
      {
        name: 'fieldsOfStudy',
        type: 'array',
        description: 'Filter by academic fields',
        required: false,
        examples: [['Computer Science'], ['Medicine', 'Biology'], ['Physics']],
      },
      {
        name: 'openAccessPdf',
        type: 'boolean',
        description: 'Only return papers with open access PDFs',
        required: false,
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { 
      operation, 
      query, 
      paperId, 
      authorId, 
      limit = 10, 
      offset = 0,
      fields,
      year,
      fieldsOfStudy,
      openAccessPdf
    } = params;
    
    console.log('[semantic_scholar_search] Operation:', operation);
    console.log('[semantic_scholar_search] Params:', params);
    
    try {
      let url: string;
      const fetchOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      };
      
      // Build fields parameter
      const defaultFields = ['title', 'authors', 'year', 'abstract', 'citationCount', 'url'];
      const requestFields = fields && fields.length > 0 ? fields : defaultFields;
      const fieldsParam = requestFields.join(',');
      
      // Build URL based on operation
      switch (operation) {
        case 'search_papers': {
          // Build query parameters
          const queryParams = new URLSearchParams({
            query: query,
            limit: limit.toString(),
            offset: offset.toString(),
            fields: fieldsParam
          });
          
          // Add optional filters
          if (year) {
            queryParams.append('year', year);
          }
          if (fieldsOfStudy && Array.isArray(fieldsOfStudy) && fieldsOfStudy.length > 0) {
            queryParams.append('fieldsOfStudy', fieldsOfStudy.join(','));
          }
          if (openAccessPdf === true) {
            queryParams.append('openAccessPdf', '');
          }
          
          url = `${SEMANTIC_SCHOLAR_BASE}/paper/search?${queryParams.toString()}`;
          break;
        }
        
        case 'get_paper': {
          // Encode paper ID (may contain special characters like DOI)
          const encodedPaperId = encodeURIComponent(paperId);
          url = `${SEMANTIC_SCHOLAR_BASE}/paper/${encodedPaperId}?fields=${fieldsParam}`;
          break;
        }
        
        case 'get_author': {
          url = `${SEMANTIC_SCHOLAR_BASE}/author/${authorId}?fields=name,aliases,affiliations,homepage,paperCount,citationCount,hIndex,papers,papers.title,papers.year,papers.citationCount`;
          break;
        }
        
        case 'get_citations': {
          const encodedPaperId = encodeURIComponent(paperId);
          url = `${SEMANTIC_SCHOLAR_BASE}/paper/${encodedPaperId}/citations?fields=${fieldsParam}&limit=${limit}&offset=${offset}`;
          break;
        }
        
        case 'get_references': {
          const encodedPaperId = encodeURIComponent(paperId);
          url = `${SEMANTIC_SCHOLAR_BASE}/paper/${encodedPaperId}/references?fields=${fieldsParam}&limit=${limit}&offset=${offset}`;
          break;
        }
        
        default:
          return {
            status: 'error',
            message: 'Invalid operation',
            error: {
              code: 'invalid_operation',
              category: 'validation',
              details: `Unknown operation: ${operation}`
            },
            format: 'plaintext'
          };
      }
      
      console.log('[semantic_scholar_search] Fetching:', url);
      
      // Make API request
      const response = await fetch(url, fetchOptions);
      
      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || '60';
        return {
          status: 'error',
          message: 'Rate limit exceeded',
          error: {
            code: 'rate_limited',
            category: 'execution',
            details: `Too many requests. Please wait ${retryAfter} seconds before trying again.`,
            retryable: true
          },
          format: 'plaintext'
        };
      }
      
      // Handle not found
      if (response.status === 404) {
        return {
          status: 'error',
          message: 'Resource not found',
          error: {
            code: 'not_found',
            category: 'execution',
            details: operation === 'get_paper' 
              ? `Paper not found: ${paperId}` 
              : operation === 'get_author'
              ? `Author not found: ${authorId}`
              : 'Resource not found'
          },
          format: 'plaintext'
        };
      }
      
      // Handle other errors
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[semantic_scholar_search] API error:', response.status, errorText);
        
        return {
          status: 'error',
          message: 'Semantic Scholar API request failed',
          error: {
            code: 'api_error',
            category: 'execution',
            details: `HTTP ${response.status}: ${errorText || 'Unknown error'}`
          },
          format: 'plaintext'
        };
      }
      
      const data = await response.json();
      console.log('[semantic_scholar_search] Success:', operation);
      
      // Format response based on operation
      let resultData: any;
      let message: string;
      
      switch (operation) {
        case 'search_papers': {
          const papers = data.data || [];
          resultData = {
            query,
            papers,
            total: data.total || papers.length,
            offset,
            limit,
            filters: {
              year,
              fieldsOfStudy,
              openAccessPdf
            }
          };
          message = `Found ${papers.length} papers for "${query}"${data.total > papers.length ? ` (${data.total} total in database)` : ''}`;
          break;
        }
        
        case 'get_paper': {
          resultData = {
            paper: data,
            paperId
          };
          message = `Retrieved paper: ${data.title || paperId}`;
          break;
        }
        
        case 'get_author': {
          const papers = data.papers || [];
          resultData = {
            author: {
              authorId: data.authorId,
              name: data.name,
              aliases: data.aliases,
              affiliations: data.affiliations,
              homepage: data.homepage,
              paperCount: data.paperCount,
              citationCount: data.citationCount,
              hIndex: data.hIndex
            },
            papers: papers.slice(0, limit),
            totalPapers: data.paperCount || papers.length
          };
          message = `Retrieved author: ${data.name} (${data.paperCount || 0} papers, ${data.citationCount || 0} citations)`;
          break;
        }
        
        case 'get_citations': {
          const citations = (data.data || []).map((item: any) => item.citingPaper);
          resultData = {
            paperId,
            citations,
            total: data.total || citations.length,
            offset,
            limit
          };
          message = `Found ${citations.length} citations${data.total > citations.length ? ` (${data.total} total)` : ''}`;
          break;
        }
        
        case 'get_references': {
          const references = (data.data || []).map((item: any) => item.citedPaper);
          resultData = {
            paperId,
            references,
            total: data.total || references.length,
            offset,
            limit
          };
          message = `Found ${references.length} references${data.total > references.length ? ` (${data.total} total)` : ''}`;
          break;
        }
        
        default:
          resultData = data;
          message = 'Operation completed';
      }
      
      return {
        status: 'success',
        message,
        data: resultData,
        format: 'json'
      };
      
    } catch (error: any) {
      console.error('[semantic_scholar_search] Error:', error);
      
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return {
          status: 'error',
          message: 'Network error',
          error: {
            code: 'network_error',
            category: 'execution',
            details: 'Unable to connect to Semantic Scholar API. Please check your internet connection.',
            retryable: true
          },
          format: 'plaintext'
        };
      }
      
      return {
        status: 'error',
        message: 'Failed to search Semantic Scholar',
        error: {
          code: 'unknown_error',
          category: 'execution',
          details: error.message || 'Unknown error occurred'
        },
        format: 'plaintext'
      };
    }
  }
};
